import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { prints, numberWord } from '../content';
import { MaskedLine, FadeUp } from '../components/Reveal';
import InstagramGrid from '../components/InstagramGrid';
import InstagramPeek from '../components/InstagramPeek';

const featured = [
    { art: 'art-23', n: '01' },
    { art: 'art-11', n: '02' },
    { art: 'art-09', n: '03' },
];

const Home = () => {
    const { lang, t } = useLanguage();
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
    const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
    const textY = useTransform(scrollYProgress, [0, 1], ['0%', '60%']);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <main data-testid="home-page">
            {/* ── Kinetic hero ─────────────────────────── */}
            <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
                <motion.div className="absolute inset-0" style={{ y: imgY }}>
                    <motion.img
                        src="/images/art-04.jpg"
                        alt={t.hero.kicker}
                        className="w-full h-[115%] object-cover"
                        initial={{ scale: 1.15 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 2.6, ease: [0.22, 1, 0.36, 1] }}
                        data-testid="hero-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
                </motion.div>

                <motion.div
                    className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col justify-end pt-32 pb-24 md:pb-28"
                    style={{ y: textY, opacity: fade }}
                >
                    <MaskedLine delay={0.5}>
                        <span className="text-[11px] md:text-xs tracking-[0.4em] uppercase text-dusk" data-testid="hero-kicker">
                            {t.hero.kicker}
                        </span>
                    </MaskedLine>
                    <h1 className="mt-5 font-serif text-paper tracking-tighter leading-[0.95] text-[13vw] sm:text-[10vw] lg:text-[7rem] xl:text-[8rem]" data-testid="hero-title">
                        {t.hero.lines.map((line, i) => (
                            <MaskedLine key={line} delay={0.7 + i * 0.15}>
                                <span className={i === 1 ? 'italic text-dusk' : ''}>{line}</span>
                            </MaskedLine>
                        ))}
                    </h1>
                    <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6">
                        <MaskedLine delay={1.3}>
                            <span className="flex flex-wrap items-center gap-4">
                                <Link
                                    to="/gallery"
                                    className="group inline-flex items-center gap-3 rounded-full bg-paper px-7 py-3.5 text-sm font-medium text-ink transition-colors duration-300 hover:bg-dusk"
                                    data-testid="hero-gallery-cta"
                                >
                                    {t.hero.ctaGallery}
                                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </Link>
                                <Link
                                    to="/shop"
                                    className="inline-flex items-center gap-3 rounded-full border border-white/30 px-7 py-3.5 text-sm text-paper transition-colors duration-300 hover:border-paper"
                                    data-testid="hero-shop-cta"
                                >
                                    {t.hero.ctaShop}
                                </Link>
                            </span>
                        </MaskedLine>
                    </div>
                </motion.div>

                <motion.div
                    className="absolute bottom-8 right-6 md:right-12 z-10 flex items-center gap-3 text-fog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2, duration: 1 }}
                >
                    <span className="text-[10px] tracking-[0.35em] uppercase">{t.hero.scroll}</span>
                    <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}>
                        <ArrowDown className="w-4 h-4" />
                    </motion.span>
                </motion.div>
            </section>

            {/* ── Instagram latest post peek ─────────── */}
            <InstagramPeek />

            {/* ── Statement ────────────────────────────── */}
            <section className="px-6 md:px-12 py-32 md:py-44 max-w-[1600px] mx-auto" data-testid="statement-section">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <FadeUp className="md:col-span-2">
                        <span className="text-xs tracking-[0.35em] uppercase text-dusk">01 — Manifeste</span>
                    </FadeUp>
                    <div className="md:col-span-9">
                        <FadeUp delay={0.1}>
                            <p className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-paper max-w-4xl" data-testid="statement-text">
                                {t.home.statement}
                            </p>
                        </FadeUp>
                        <FadeUp delay={0.25}>
                            <Link
                                to="/about"
                                className="group mt-10 inline-flex items-center gap-3 text-sm text-dusk link-line w-fit"
                                data-testid="statement-about-link"
                            >
                                {t.home.readManifesto}
                                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* ── Featured works ───────────────────────── */}
            <section className="px-6 md:px-12 pb-32 md:pb-44 max-w-[1600px] mx-auto" data-testid="featured-section">
                <div className="flex items-end justify-between gap-8 mb-16 md:mb-24">
                    <FadeUp>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-paper" data-testid="featured-title">
                            {t.home.featuredTitle}
                        </h2>
                        <p className="mt-4 text-sm md:text-base text-fog">{t.home.featuredSub}</p>
                    </FadeUp>
                    <FadeUp delay={0.15} className="hidden md:block shrink-0">
                        <Link to="/gallery" className="group inline-flex items-center gap-3 text-sm text-paper link-line" data-testid="featured-gallery-link">
                            {t.home.viewAll}
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </FadeUp>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
                    {featured.map((f, i) => {
                        return (
                            <FadeUp
                                key={f.art}
                                delay={i * 0.12}
                                className={i === 1 ? 'md:col-span-7 md:mt-24' : 'md:col-span-5'}
                            >
                                <Link to="/gallery" className="group block" data-testid={`featured-work-${f.n}`}>
                                    <div className="relative spotlight">
                                        <div className="overflow-hidden">
                                            <img
                                                src={`/images/${f.art}.jpg`}
                                                alt={numberWord(i + 1, lang)}
                                                className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                                                loading="lazy"
                                            />
                                        </div>
                                        <span className="absolute -top-5 -left-2 font-serif italic text-6xl text-dusk/60 select-none" aria-hidden>
                                            {f.n}
                                        </span>
                                    </div>
                                    <div className="mt-5 flex items-baseline justify-between">
                                        <p className="font-serif italic text-xl tracking-tight text-paper lowercase">{numberWord(i + 1, lang)}</p>
                                        <p className="text-xs tracking-[0.25em] uppercase text-fog">Plein air</p>
                                    </div>
                                </Link>
                            </FadeUp>
                        );
                    })}
                </div>

                <FadeUp className="mt-14 md:hidden">
                    <Link to="/gallery" className="group inline-flex items-center gap-3 text-sm text-paper link-line" data-testid="featured-gallery-link-mobile">
                        {t.home.viewAll}
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </FadeUp>
            </section>

            {/* ── Prints teaser ────────────────────────── */}
            <section className="border-t border-white/10 bg-surface" data-testid="prints-teaser-section">
                <div className="px-6 md:px-12 py-32 md:py-40 max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-5">
                        <FadeUp>
                            <span className="text-xs tracking-[0.35em] uppercase text-dusk">02 — {t.nav.shop}</span>
                        </FadeUp>
                        <FadeUp delay={0.1}>
                            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-paper" data-testid="prints-teaser-title">
                                {t.home.shopTitle}
                            </h2>
                        </FadeUp>
                        <FadeUp delay={0.2}>
                            <p className="mt-6 text-base text-fog leading-relaxed max-w-md">{t.home.shopSub}</p>
                        </FadeUp>
                        <FadeUp delay={0.3}>
                            <Link
                                to="/shop"
                                className="group mt-10 inline-flex items-center gap-3 rounded-full bg-dusk px-7 py-3.5 text-sm font-medium text-ink transition-colors duration-300 hover:bg-dusk-hover"
                                data-testid="prints-teaser-cta"
                            >
                                {t.home.shopCta}
                                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </FadeUp>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-3 gap-4 md:gap-6">
                        {prints.slice(0, 3).map((p, i) => (
                            <FadeUp key={p.slug} delay={0.15 + i * 0.12} className={i === 1 ? 'mt-10' : ''}>
                                <Link to="/shop" className="group block" data-testid={`teaser-print-${p.slug}`}>
                                    <div className="overflow-hidden">
                                        <img
                                            src={`/images/${p.art}.jpg`}
                                            alt={numberWord(i + 1, lang)}
                                            className="w-full aspect-[3/4] object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                                            loading="lazy"
                                        />
                                    </div>
                                    <p className="mt-4 font-serif italic text-sm text-paper lowercase">
                                        {numberWord(i + 1, lang)}
                                    </p>
                                </Link>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Instagram feed ──────────────────────── */}
            <InstagramGrid />
        </main>
    );
};

export default Home;
